import React, { useCallback, useContext, MouseEventHandler } from 'react'
import { rem } from 'polished'
import cn from 'clsx'
import styled from 'styled-components'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import TextLink from 'components/Luxkit/TextLink'
import Heading from 'components/Luxkit/Typography/Heading'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import { themeClassName } from 'lib/theme/themeUtils'

const HostImage = styled(ResponsiveImage)`
  border-radius: 50%;
  overflow: hidden;
`

const ModalBody = styled.div`
  padding: ${rem(24)};
`

const HostTile = styled(Clickable)`
  display: flex;
  flex-direction: column;

  &.variant-large {
    min-width: ${rem(180)};
  }

  &.variant-small {
    min-width: ${rem(112)};
  }
`

interface Props {
  name: string;
  description: string;
  imageId: string;
  variant: 'small' | 'large'
  modalImageId?: string;
}

function HostCard({ name, description, imageId, variant, modalImageId }: Props) {
  const showModal = useContext(ModalContext)
  const displayModal = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    showModal(<ModalBase className={themeClassName('default')}>
      <ModalHeader title={name} />
      <ResponsiveImage fit="center" gravity="north" dpr={2} id={modalImageId ?? imageId} />
      <ModalBody>
        <BodyText variant="medium">{description}</BodyText>
      </ModalBody>
    </ModalBase>)
  }, [description, imageId, modalImageId, name, showModal])

  return (
    <HostTile onClick={displayModal} className={cn(`variant-${variant}`)}>
      <Group gap={variant === 'large' ? 16 : 8} direction="vertical" horizontalAlign="center" className={cn(`variant-${variant}`)}>
        <HostImage gravity="north" width={variant === 'large' ? 100 : 60} aspectRatio="1" id={imageId} />
        <Group direction="vertical" horizontalAlign="center">
          {variant === 'large' && <Heading align="center" variant="heading5" wrap="word-wrap">{name}</Heading>}
          {variant === 'small' && <BodyText align="center" variant="medium" colour="neutral-two">{name}</BodyText>}
          <TextLink size="medium" weight="regular">Learn more</TextLink>
        </Group>
      </Group >
    </HostTile>
  )
}

HostCard.defaultProps = {
  variant: 'large',
}

export default HostCard
